/**
 * FlowPlannerGridConfig.js
 *
 * UI configuration for the Flow Planner grid.
 *
 * PROCESS_HIERARCHY is now built dynamically from admin.json via
 * buildProcessHierarchy() in FlowPlannerModel.js.
 *
 * This file retains:
 *   - DISPLAY_OVERRIDES: UI cosmetic labels (process, group, variant, controller)
 *   - GROUP_SKELETON:    Row ordering + carve-out definitions
 *   - FLOW_PROCESSES / DEFAULT_MULTIPLIER: Algorithm constants
 */

// ─── Constants ──────────────────────────────────────────────────────────────

export const FLOW_PROCESSES = ['INDUCT', 'STOW', 'PICK'];
export const DEFAULT_MULTIPLIER = 1.15;

// ─── Display overrides ──────────────────────────────────────────────────────
// Single map for ALL UI cosmetic labels: group names, controller names,
// process display names, and automation variant names.
// Key = admin.json canonical name; Value = UI label shown in grid.

export const DISPLAY_OVERRIDES = {
  // Group display names (key → UI label)
  WATER_SPIDER: 'Inbound Dock W/S',
  INDUCT_GROUP: 'Induct Group',
  DIVERT: 'Divert',
  STOW: 'Stow',
  PICK_TO_BUFFER: 'Pick to Buffer',
  PROBLEM_SOLVE: 'Sort Problem Solve',
  NON_CON_STOW: 'Non Con Stow',
  OUTBOUND: 'Outbound',

  // Controller name (executionType → UI label)
  MECHANICAL: 'Manual',

  // Process display names (rateName → UI label)
  'Auto Scan Induct Loader': 'ASL Induct Loader',
  'Auto Scan Pusher': 'ASL Pusher',
  Labeler: 'ASML Labeler',
  'Pick Stage & Dispatch Problem Solve': 'Pick Stage Dispatch Problem Solve',
  'Yard Assist and Cart Handling': 'Yard Assist & Cart Handling',

  // Automation variant display names (rateName_executionType → UI label)
  Diverter_MECHANICAL: 'Manual Diverter',
  Diverter_ADTA: 'Auto Divert Straightener',
  'Pick to Buffer_MECHANICAL': 'Pick to Buffer (Manual)',
  'Pick to Buffer_ADTA': 'ADTA Pick to Buffer',
  Stow_MECHANICAL: 'Container Building'
};

// ─── Group skeleton ─────────────────────────────────────────────────────────
// Defines UI row ordering and group composition.
// Children are populated dynamically from admin.json by buildProcessHierarchy().
//
// Fields:
//   key               — Expand-state tracking key
//   processes          — Explicit process name list (for carve-out groups)
//   fromExecutionFlow  — Build controllers/automation from executionFlows[name]
//   sharesAutomation   — Borrow automation split from another executionFlow
//   fromPhase          — Include all processes with this allocationPhase
//   type: 'wip'        — UI-only WIP marker row

export const GROUP_SKELETON = [
  { key: 'WATER_SPIDER', processes: ['Inbound Dock W/S'] },
  { key: 'INDUCT_GROUP', fromExecutionFlow: 'Induct' },
  { key: 'DIVERT', fromExecutionFlow: 'Divert' },
  {
    key: 'PICK_TO_BUFFER',
    processes: ['Pick to Buffer', 'Auto Divert Straightener', 'ADTA Pick to Buffer'],
    sharesAutomation: 'Stow'
  },
  { key: 'STOW_WIP', type: 'wip' },
  { key: 'STOW', fromExecutionFlow: 'Stow' },
  { key: 'PROBLEM_SOLVE', processes: ['Sort Problem Solve'] },
  { key: 'NON_CON_STOW', processes: ['Non-Con Manual Handling', 'Non-Scan Stow'] },
  { key: 'OUTBOUND', fromPhase: 'PICK_AND_STAGE', exclude: ['Pick Stage Problem Solve', 'UTR Dispatch'] }
];

// ─── Volume source override ─────────────────────────────────────────────────
// processGroup → laborProcessGroups[].name gives volumeSource for all groups.
// Only DISPATCH_GROUP needs a remap (shares PICK volume).

export const VOLUME_SOURCE_OVERRIDES = { DISPATCH_GROUP: 'PICK' };

// ─── DYNAMIC_V2 key overrides ───────────────────────────────────────────────
// DYNAMIC_V2 requiredHeadcounts uses different keys than STATIC_V3 for some processes.
// Map: STATIC_V3 key → DYNAMIC_V2 key
export const DYNAMIC_V2_KEY_OVERRIDES = {
  Stow: 'Container Building',
  Induct: 'Label'
};
